/**
 * Wat de computer van een ruilaanbod vindt.
 *
 * Het origineel gooit hier geen munt op. In `90-speldata/ai/1/4_Rohstoffpraeferenz.ki`
 * rekent Klaus Teuber per grondstof een **voorkeur** uit: hij legt zijn voorraad naast
 * wat zijn eerstvolgende bouwplannen kosten, en houdt per grondstof een getal over.
 * Negatief betekent "hier kom ik tekort", positief "hiervan heb ik over". In het blad
 * staat bijvoorbeeld leem op −18,9 bij een voorraad van nul, en goud op +20 bij een
 * voorraad van twee: leem wil hij hebben, goud geeft hij graag weg.
 *
 * Wij rekenen hetzelfde idee eenvoudiger uit — één bouwplan vooruit in plaats van drie,
 * zonder de gewichtenmatrix — maar met dezelfde uitkomst: een aanbod wordt aangenomen als
 * het je verder helpt dan het je kost. Het blijft dus verklaarbaar in plaats van
 * willekeurig, en dat is precies wat een tegenstander geloofwaardig maakt.
 */
import { BASIC_CARDS, type Resource } from '@catan/contracts';
import {
  resourcePool,
  zelfdeBundel,
  type GameState,
  type PlayerState,
  type Seat,
} from '@catan/rules';
import { persoonlijkheidVoor } from './persoonlijkheid.js';

const SOORTEN: readonly Resource[] = ['hout', 'klei', 'wol', 'graan', 'erts', 'goud'];

/** Een bundel grondstoffen; `undefined` telt als nul. */
type Bundel = Readonly<Partial<Record<Resource, number | undefined>>>;

/** De kosten van alles wat deze speler binnenkort zou willen neerzetten. */
function bouwplannen(player: PlayerState): Bundel[] {
  const plannen: Bundel[] = [];
  for (const naam of ['Straat', 'Dorp', 'Stad'] as const) {
    const kaart = BASIC_CARDS.find((c) => c.name === naam);
    if (kaart !== undefined) plannen.push(kaart.cost);
  }
  // En wat er in de hand zit: die kaarten wil hij bouwen, daar heeft hij ze voor.
  for (const id of player.hand) {
    const kaart = BASIC_CARDS.find((c) => c.id === id);
    if (kaart !== undefined && kaart.kind === 'uitbouw') plannen.push(kaart.cost);
  }
  return plannen;
}

/**
 * Hoeveel is elke grondstof deze speler waard?
 *
 * Hoog = "die wil ik hebben". De waarde is het aantal exemplaren dat hij nog tekort komt
 * over al zijn bouwplannen, min een kleine korting voor wat hij al op de plank heeft
 * liggen — want de tiende houtblok is minder waard dan de eerste.
 */
export function grondstofwaarde(player: PlayerState): Record<Resource, number> {
  const voorraad = resourcePool(player);
  const waarde = Object.fromEntries(SOORTEN.map((s) => [s, 0])) as Record<Resource, number>;

  for (const plan of bouwplannen(player)) {
    for (const soort of SOORTEN) {
      const nodig = plan[soort] ?? 0;
      if (nodig === 0) continue;
      const tekort = Math.max(0, nodig - (voorraad[soort] ?? 0));
      waarde[soort] += tekort;
    }
  }
  for (const soort of SOORTEN) {
    // Overschot maakt een grondstof goedkoper om weg te geven.
    waarde[soort] -= (voorraad[soort] ?? 0) * 0.25;
  }
  return waarde;
}

/**
 * De grondstoffen waarvan hij er nog precies één mist om iets te kunnen bouwen.
 *
 * Dit is `Fehlende Rohstoffe zu ersten Bauvorhaben = −1` uit `23_aktiverHandel.ki` (rij 4,
 * gebruikt in rij 26): het origineel doet zijn eenvoudige aanbod alléén als er nog één
 * enkele grondstof tussen hem en zijn bouwplan in staat. Dat is het verschil tussen
 * "hier heb ik wat aan" en de hele beurt staan pingelen op elk klein voordeeltje.
 */
function eenGrondstofTekort(player: PlayerState): Resource[] {
  const voorraad = resourcePool(player);
  const missend = new Set<Resource>();
  for (const plan of bouwplannen(player)) {
    const gaten = SOORTEN.flatMap((soort) => {
      const mist = Math.max(0, (plan[soort] ?? 0) - (voorraad[soort] ?? 0));
      return Array.from({ length: mist }, () => soort);
    });
    const enige = gaten[0];
    if (gaten.length === 1 && enige !== undefined) missend.add(enige);
  }
  return [...missend];
}

/** Het meeste dat één enkel bouwplan van deze grondstof vraagt. */
function meesteNodig(player: PlayerState): Record<Resource, number> {
  const nodig = Object.fromEntries(SOORTEN.map((s) => [s, 0])) as Record<Resource, number>;
  for (const plan of bouwplannen(player)) {
    for (const soort of SOORTEN) {
      const vraagt = plan[soort] ?? 0;
      if (vraagt > nodig[soort]) nodig[soort] = vraagt;
    }
  }
  return nodig;
}

export interface Aanbod {
  readonly give: Bundel;
  readonly want: Bundel;
}

/**
 * Neemt de computer dit aanbod aan?
 *
 * Vanuit zijn kant bekeken: hij *krijgt* wat de aanbieder weggeeft en *levert* wat de
 * aanbieder vraagt. Hij zegt ja als dat hem per saldo verder helpt.
 */
export function neemtAanbodAan(state: GameState, seat: Seat, aanbod: Aanbod): boolean {
  const speler = state.players[seat];
  const waarde = grondstofwaarde(speler);
  const som = (bundel: Bundel): number =>
    SOORTEN.reduce((totaal, soort) => totaal + (bundel[soort] ?? 0) * waarde[soort], 0);

  const krijgt = som(aanbod.give);
  const geeft = som(aanbod.want);
  // Gelijkspel is geen reden om te ruilen; hij wil er iets aan overhouden — en wie een
  // hoge afgeefgrens heeft, wil er méér aan overhouden. Bernadette is de enige.
  return krijgt - geeft > winstEis(speler.name);
}

/**
 * Welk aanbod doet de computer zelf?
 *
 * Het origineel doet er tot vijf per beurt (`23_aktiverHandel.ki`, rijen 26-30): eerst een
 * eenvoudig aanbod, dan een tweede, dan een met twee grondstoffen, en tenslotte twee
 * pogingen om juist die ene grondstof los te krijgen waarmee hij bij de bank 2:1 of 3:1
 * kan ruilen.
 *
 * Wij doen de eerste vorm: hij biedt zijn meest ontbeerlijke grondstof aan voor zijn
 * meest gemiste, en alleen als de ander die ook echt heeft liggen. Vragen om iets wat er
 * niet is, is geen aanbod maar ruis.
 *
 * Wijs je hem af, dan schrapt hij dat voorstel en pakt hij het volgende uit zijn rijtje.
 * Zonder dat geheugen bood hij vijf keer achter elkaar precies hetzelfde aan — de stand
 * verandert immers niet van een "nee", dus zijn beste voorstel ook niet.
 *
 * En hij houdt twee grenzen aan, want daar zit in het origineel de rem. In
 * `8_Strategie1.ki` staan per tegenstander twee getallen — bij Chi `Forderungsgrenze =
 * −6` en `Abgabegrenze = 5` — en elk aanbod in `23_aktiverHandel.ki` (rijen 26-30) moet
 * er allebei langs. Hij vraagt dus pas iets als hij er écht mee vastzit, en geeft pas
 * iets weg als geen enkel plan het nodig heeft. Zonder die twee grenzen was elk piepklein
 * voordeel al genoeg reden om te bieden, en dat is precies waarom hij elke beurt stond
 * te leuren.
 */
/**
 * De twee remmen uit `8_Strategie1.ki`, vertaald naar onze schaal.
 *
 * In het blad staan `Forderungsgrenze` en `Abgabegrenze` als getallen in de eenheid van de
 * sheet zelf — en die eenheid hebben we niet, want de omliggende bladen rekenen in hun
 * eigen maat. Wat we wél hebben is de **rangorde**, en die is eenduidig: zes van de zeven
 * tegenstanders staan op −6 en 5, en alleen Bernadette op −8 en 10. Dat is haar bio in
 * cijfers: "onderhandelen kun je niet met haar".
 *
 * Hieronder staat dus onze schaal, met hun volgorde. Wie op de standaardwaarden staat
 * gedraagt zich precies zoals voorheen; wie strenger staat, biedt minder en neemt minder
 * aan. Loopt er ooit een derde waarde binnen, dan schuift dit vanzelf mee.
 */
const STANDAARD_VRAAGGRENS = -6;
const STANDAARD_AFGEEFGRENS = 5;

/** Hoeveel een ruil hem minimaal moet opleveren voor hij hem doet. */
function winstEis(naam: string): number {
  const profiel = persoonlijkheidVoor(naam);
  if (profiel === undefined) return 0;
  return Math.max(0, STANDAARD_VRAAGGRENS - profiel.vraaggrens);
}

/** Hoeveel hij bovenop zijn duurste plan wil overhouden voor hij iets weggeeft. */
function afgeefmarge(naam: string): number {
  const profiel = persoonlijkheidVoor(naam);
  if (profiel === undefined) return 1;
  return Math.max(1, Math.round(profiel.afgeefgrens / STANDAARD_AFGEEFGRENS));
}

export function bedenktAanbod(
  state: GameState,
  seat: Seat,
): { give: Partial<Record<Resource, number>>; want: Partial<Record<Resource, number>> } | undefined {
  const ik = state.players[seat];
  const ander = state.players[seat === 0 ? 1 : 0];
  const waarde = grondstofwaarde(ik);
  const mijn = resourcePool(ik);
  const hun = resourcePool(ander);

  const nodig = meesteNodig(ik);

  // Abgabegrenze: weggeven mag alleen wat hij overhoudt als zijn duurste plan betaald is.
  // Hoe hoger die grens, hoe meer hij wil overhouden voor hij iets afstaat.
  const marge = afgeefmarge(ik.name);
  const teGeven = SOORTEN.filter((s) => (mijn[s] ?? 0) >= nodig[s] + marge).sort(
    (a, b) => waarde[a] - waarde[b],
  );
  // Forderungsgrenze: vragen doet hij alleen naar de grondstof die hem nú tegenhoudt —
  // en die de ander ook echt heeft liggen.
  const teVragen = eenGrondstofTekort(ik).filter((s) => (hun[s] ?? 0) > 0);

  // Elk ruilbaar paar dat hem vooruit helpt, het aantrekkelijkste eerst. Wat hij er niet
  // beter van wordt komt er niet in: dan houdt hij zijn mond.
  type Voorstel = {
    give: Partial<Record<Resource, number>>;
    want: Partial<Record<Resource, number>>;
    winst: number;
  };
  const kandidaten: Voorstel[] = [];
  for (const geef of teGeven) {
    for (const wil of teVragen) {
      // Forderungsgrenze: hoe strenger die staat, hoe meer een ruil hem moet opleveren
      // voordat hij er zijn mond voor opendoet.
      if (geef === wil || waarde[wil] - waarde[geef] <= winstEis(ik.name)) continue;
      kandidaten.push({ give: { [geef]: 1 }, want: { [wil]: 1 }, winst: waarde[wil] - waarde[geef] });
    }
  }
  kandidaten.sort((a, b) => b.winst - a.winst);

  const gepasseerd = kandidaten.find(
    (k) =>
      !state.afgewezenAanbiedingen.some(
        (eerder) =>
          eerder.from === seat &&
          zelfdeBundel(eerder.give, k.give) &&
          zelfdeBundel(eerder.want, k.want),
      ),
  );
  if (gepasseerd === undefined) return undefined;
  return { give: gepasseerd.give, want: gepasseerd.want };
}
